/* TESERY cart drawer content compatibility patch. */
(() => {
  const cartTitleSelector = "#MinimogCartDrawer .m-cart-drawer__title";
  const discountSelector =
    "#MinimogCartDrawerFooter [data-minimog-cart-discounts]";
  let discountSyncTimer;
  let discountRenderSignature = "";
  let discountRequestId = 0;

  const getItemTitle = (item) =>
    item?.querySelector(".m-cart-item__title")?.textContent.trim() || "this item";

  const updateCartTitle = () => {
    document.querySelectorAll(cartTitleSelector).forEach((title) => {
      if (title.textContent.trim() !== "Cart") {
        title.textContent = "Cart";
      }
    });
  };

  const alignCheckoutOrder = () => {
    document
      .querySelectorAll(
        "#MinimogCartDrawerFooter .m-cart-drawer__link-to-cart",
      )
      .forEach((viewCart) => {
        const actionGroup = viewCart.parentElement;
        if (!actionGroup) return;

        const checkout = Array.from(actionGroup.children).find(
          (element) =>
            element.matches(
              "button.xcotton-checkout, button.m-button--primary",
            ),
        );

        if (checkout && viewCart.nextElementSibling !== checkout) {
          actionGroup.insertBefore(viewCart, checkout);
        }
      });
  };

  const enhanceQuantityControls = () => {
    document
      .querySelectorAll("#MinimogCartDrawer .m-cart-drawer-item")
      .forEach((item) => {
        const title = getItemTitle(item);
        const buttons = item.querySelectorAll(
          ".m-cart-drawer-item__quantity .m-quantity__button",
        );
        const input = item.querySelector(
          ".m-cart-drawer-item__quantity .m-quantity__input",
        );

        buttons.forEach((button, index) => {
          button.type = "button";
          button.setAttribute(
            "aria-label",
            `${index === 0 ? "Decrease" : "Increase"} quantity for ${title}`,
          );
        });

        if (input) {
          input.setAttribute("inputmode", "numeric");
          input.setAttribute("aria-label", `Quantity for ${title}`);
        }
      });
  };

  const enhanceRemoveControls = () => {
    document
      .querySelectorAll("#MinimogCartDrawer .m-cart-item__remove")
      .forEach((remove) => {
        const item = remove.closest(".m-cart-drawer-item");
        const title = getItemTitle(item);

        if (remove.matches("button")) {
          remove.type = "button";
        }

        remove.setAttribute("aria-label", `Remove ${title} from cart`);

        if (
          !remove.matches("a[href], button, input, select, textarea") &&
          remove.dataset.teseryKeyboardReady !== "true"
        ) {
          remove.setAttribute("role", "button");
          remove.tabIndex = 0;
          remove.dataset.teseryKeyboardReady = "true";
          remove.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            remove.click();
          });
        }
      });
  };

  const enhanceLiveUpdates = () => {
    document
      .querySelectorAll("#MinimogCartDrawerFooter .m-cart__subtotal")
      .forEach((subtotal) => {
        subtotal.setAttribute("role", "status");
        subtotal.setAttribute("aria-live", "polite");
        subtotal.setAttribute("aria-atomic", "true");
      });
  };

  const syncCartBadgeLabels = () => {
    document.querySelectorAll(".m-cart-icon-bubble").forEach((cartLink) => {
      const count = cartLink.querySelector(".m-cart-count-bubble");
      if (!count) return;

      const visibleCount = count.textContent.trim();
      if (visibleCount && cartLink.getAttribute("aria-label") !== visibleCount) {
        cartLink.setAttribute("aria-label", visibleCount);
      }
    });
  };

  const syncAddonPanelState = () => {
    document.querySelectorAll("#MinimogCartDrawerFooter").forEach((footer) => {
      footer.classList.toggle(
        "tesery-cart-addon-open",
        Boolean(footer.querySelector(".m-cart-addon__body.open")),
      );
    });
  };

  const formatCartMoney = (amount, currency, showCurrencyCode) => {
    const locale = document.documentElement.lang || navigator.language || "en";
    let formatted;

    try {
      formatted = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(amount / 100);
    } catch (_error) {
      formatted = `${(amount / 100).toFixed(2)} ${currency}`;
    }

    if (
      showCurrencyCode &&
      currency &&
      !formatted.toUpperCase().includes(currency.toUpperCase())
    ) {
      formatted += ` ${currency}`;
    }

    return formatted;
  };

  const renderDiscountSubtotal = (cart) => {
    const discountContainer = document.querySelector(discountSelector);
    const subtotalPrice = document.querySelector(
      "#MinimogCartDrawerFooter [data-cart-subtotal-price]",
    );

    if (!discountContainer || !subtotalPrice) return;

    const hasRenderedDiscount = Boolean(discountContainer.textContent.trim());
    const finalTotal = Number(cart?.total_price);
    const reportedOriginal = Number(cart?.original_total_price);
    const reportedDiscount = Number(cart?.total_discount);
    const originalTotal =
      reportedOriginal > finalTotal
        ? reportedOriginal
        : finalTotal + Math.max(0, reportedDiscount || 0);
    const hasRealDiscount =
      hasRenderedDiscount &&
      Number.isFinite(finalTotal) &&
      Number.isFinite(originalTotal) &&
      originalTotal > finalTotal;

    if (!hasRealDiscount) {
      discountRenderSignature = "";
      subtotalPrice.removeAttribute("data-tesery-discount-price");
      return;
    }

    const finalText =
      subtotalPrice
        .querySelector(".tesery-cart-subtotal__final")
        ?.textContent.trim() || subtotalPrice.textContent.trim();
    const currency = cart.currency || window.Shopify?.currency?.active || "USD";
    const showCurrencyCode = finalText.toUpperCase().includes(currency.toUpperCase());
    const signature = `${originalTotal}:${finalTotal}:${currency}:${finalText}`;

    if (
      discountRenderSignature === signature &&
      subtotalPrice.dataset.teseryDiscountPrice === "true"
    ) {
      return;
    }

    const originalPrice = document.createElement("del");
    originalPrice.className = "tesery-cart-subtotal__original";
    originalPrice.textContent = formatCartMoney(
      originalTotal,
      currency,
      showCurrencyCode,
    );

    const finalPrice = document.createElement("span");
    finalPrice.className = "tesery-cart-subtotal__final";
    finalPrice.textContent = finalText;

    subtotalPrice.replaceChildren(originalPrice, finalPrice);
    subtotalPrice.dataset.teseryDiscountPrice = "true";
    discountRenderSignature = signature;
  };

  const syncDiscountPricing = async () => {
    const discountContainer = document.querySelector(discountSelector);
    if (!discountContainer?.textContent.trim()) {
      discountRenderSignature = "";
      return;
    }

    const requestId = ++discountRequestId;

    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}cart.js`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (requestId !== discountRequestId) return;
      renderDiscountSubtotal(cart);
    } catch (_error) {
      /* Keep Shopify's truthful subtotal if the cart endpoint is unavailable. */
    }
  };

  const scheduleDiscountPricing = () => {
    window.clearTimeout(discountSyncTimer);
    discountSyncTimer = window.setTimeout(syncDiscountPricing, 80);
  };

  const getCartDrawerSection = async () => {
    const root = window.Shopify?.routes?.root || "/";
    const response = await fetch(`${root}cart?section_id=cart-drawer`, {
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("Cart section refresh failed");
    return new DOMParser().parseFromString(await response.text(), "text/html");
  };

  const refreshCartFragments = (sectionDocument) => {
    [
      "[data-minimog-cart-items]",
      "[data-minimog-cart-discounts]",
      "[data-cart-subtotal]",
    ].forEach((selector) => {
      const current = document.querySelector(`#MinimogCartDrawer ${selector}`);
      const updated = sectionDocument.querySelector(
        `#MinimogCartDrawer ${selector}`,
      );
      if (current && updated) current.innerHTML = updated.innerHTML;
    });
  };

  const isDiscountCodeRendered = (sectionDocument, code) => {
    const normalizedCode = code.trim().toLocaleUpperCase();
    return Array.from(
      sectionDocument.querySelectorAll(
        "#MinimogCartDrawer .m-cart__discount--item",
      ),
    ).some((item) =>
      item.textContent.trim().toLocaleUpperCase().includes(normalizedCode),
    );
  };

  const applyRealDiscountCode = async (button, code, input) => {
    button.dataset.teseryBusy = "true";
    button.setAttribute("aria-busy", "true");

    try {
      const root = window.Shopify?.routes?.root || "/";
      const cartResponse = await fetch(`${root}cart/update.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ discount: code }),
      });
      if (!cartResponse.ok) throw new Error("Discount update failed");
      const cart = await cartResponse.json();

      const sectionDocument = await getCartDrawerSection();
      const isValid = !code || isDiscountCodeRendered(sectionDocument, code);

      refreshCartFragments(sectionDocument);

      input.toggleAttribute("aria-invalid", Boolean(code) && !isValid);
      if (!isValid) {
        window.localStorage.removeItem("minimog-discount-code");
        document
          .querySelectorAll("#MinimogCartDrawer [data-discount-noti]")
          .forEach((indicator) => {
            indicator.style.display = "none";
          });
      }

      renderDiscountSubtotal(cart);
      if (
        typeof MinimogEvents !== "undefined" &&
        typeof MinimogTheme !== "undefined" &&
        MinimogEvents?.emit &&
        MinimogTheme?.pubSubEvents?.cartUpdate
      ) {
        MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
          ...cart,
          source: "tesery-discount",
        });
      }
    } catch (_error) {
      input.setAttribute("aria-invalid", "true");
    } finally {
      delete button.dataset.teseryBusy;
      button.removeAttribute("aria-busy");
      applyCartEnhancements();
    }
  };

  const handleCouponSave = (event) => {
    const button = event.target.closest(
      "#MinimogCartDrawer .m-cart-addon--save[data-action='coupon']",
    );
    if (!button || button.dataset.teseryBusy === "true") return;

    const input = button
      .closest(".m-cart-addon__body")
      ?.querySelector("input[name='discount']");
    const code = input?.value.trim() || "";
    if (!input) return;

    applyRealDiscountCode(button, code, input);
  };

  const handleAddonSwitch = (event) => {
    const trigger = event.target.closest(
      "#MinimogCartDrawer .m-cart-addon--trigger-button[data-open]",
    );
    if (!trigger) return;

    const addon = trigger.closest(".m-cart-addon");
    const targetId = `m-addons-${trigger.dataset.open}`;
    addon?.querySelectorAll(".m-cart-addon__body.open").forEach((panel) => {
      if (panel.id !== targetId) panel.classList.remove("open");
    });
  };

  const applyCartEnhancements = () => {
    updateCartTitle();
    alignCheckoutOrder();
    enhanceQuantityControls();
    enhanceRemoveControls();
    enhanceLiveUpdates();
    syncCartBadgeLabels();
    syncAddonPanelState();
    scheduleDiscountPricing();
  };

  const start = () => {
    applyCartEnhancements();

    const observer = new MutationObserver((mutations) => {
      if (
        mutations.some(
          (mutation) =>
            mutation.type === "characterData" ||
            mutation.type === "attributes" ||
            mutation.addedNodes.length > 0,
        )
      ) {
        applyCartEnhancements();
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
      characterData: true,
      childList: true,
      subtree: true,
    });
    document.addEventListener("click", handleAddonSwitch, true);
    document.addEventListener("click", handleCouponSave, true);
    document.addEventListener("shopify:section:load", applyCartEnhancements);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
